import { KeyboardEvent, useState, useEffect, useRef } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { cva, type VariantProps } from 'class-variance-authority'
import confetti from 'canvas-confetti'
import Pill from '@/components/ui/Pill'
import { Icon } from '@/icons/Icon'
import { cn } from '@/lib/utils'

const container = cva(
  'flex h-fit items-center transition-colors gap-3 border border-gray-200 rounded-lg py-2 px-2.5',
  {
    variants: {
      response: {
        success: 'border-emerald-500',
        error: 'bg-red-500/10 border-red-500',
      },
    },
  },
)

type SuggestionFormProps = VariantProps<typeof container> & {
  prefill?: string | null
  response: 'success' | 'error' | null
  setResponse: (response: 'success' | 'error' | null) => void
}

export default function SuggestionForm({ prefill, response, setResponse }: SuggestionFormProps) {
  const [suggestion, setSuggestion] = useState<string>(prefill ?? '')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (response === 'success') {
      const confettiTimer = setTimeout(() => {
        confetti({
          particleCount: 60,
          startVelocity: 30,
          spread: 40,
          origin: {
            x: 0.5,
            y: 0.6,
          },
          colors: ['10B981'],
        })
      }, 300)
      return () => clearTimeout(confettiTimer)
    }
  }, [response])

  const handleSubmit = () => {
    if (suggestion !== '') {
      setResponse('success')
    } else {
      setResponse('error')
      inputRef.current?.focus()
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleSubmit()
    }
  }

  return (
    <div className="relative mx-auto w-full max-w-[500px] shrink-0">
      <div className="flex h-fit flex-col items-center justify-center gap-3 md:flex-row md:gap-0">
        <div
          className={cn(
            'relative h-8 transition-all duration-500 ease-in-out',
            response === 'success' ? 'w-0 opacity-0' : 'w-full opacity-100',
          )}
        >
          <Icon
            name="at-sign"
            size={16}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-600"
          />
          <input
            ref={inputRef}
            type="email"
            value={suggestion}
            onChange={(e) => setSuggestion(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter suggestion"
            aria-label="Enter suggestion"
            disabled={response === 'success'}
            autoComplete="off"
            className={cn(
              'h-full w-full rounded-xl border bg-transparent bg-white pb-0.5 pl-8 text-16 text-gray-950 placeholder-gray-400 outline-none transition-colors md:text-14',
              response === 'error'
                ? 'border-red-500'
                : 'border-gray-200 focus:border-primary-500 focus:outline-primary-50',
            )}
            maxLength={100}
          />
        </div>

        <Pill
          style="primary"
          onClick={handleSubmit}
          className={cn(
            'relative mx-3 w-fit origin-right overflow-hidden transition-all duration-300 ease-in-out',
            response === 'success'
              ? 'pointer-events-none max-w-[100px] bg-emerald-500'
              : 'max-w-[200px]',
          )}
        >
          <div
            className={cn(
              'relative flex h-16 w-full flex-col text-right transition-transform duration-300 ease-in-out',
              response === 'success' ? '-translate-y-1/4' : 'translate-y-1/4',
            )}
          >
            <span className="flex h-8 items-center justify-end whitespace-nowrap">
              Submit suggestion
            </span>
            <span className="flex h-8 items-center justify-end whitespace-nowrap">Submitted!</span>
          </div>
        </Pill>
      </div>

      <AnimatePresence>
        {response && (
          <motion.span
            key={response}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            className="absolute -bottom-6 left-3"
          >
            {response === 'error' ? (
              <div className="flex items-center gap-2 text-12 text-red-500">
                <Icon name="triangle-warning" size={12} />
                Please enter a suggestion
              </div>
            ) : null}
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  )
}
